import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { candidatesAPI, aiAPI } from '../api/client';
import { toast } from 'react-toastify';
import '../pages/AnimationsAndStyles.css';

export default function CandidatesList() {
  const [candidates, setCandidates] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [parsing, setParsing] = useState(false);

  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    skills: '',
    experience: '',
    education: '',
    position: ''
  });

  const navigate = useNavigate();

  useEffect(() => {
    fetchCandidates();
  }, []);

  const fetchCandidates = async () => {
    try {
      const res = await candidatesAPI.getAll();
      setCandidates(res.data);
    } catch {
      toast.error('Failed to fetch candidates');
    }
  };

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleResumeUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setParsing(true);
    const fd = new FormData();
    fd.append('resume', file);

    try {
      const res = await aiAPI.parseResume(fd);
      setFormData(res.data);
      toast.success('Resume parsed successfully');
    } catch {
      toast.error('Resume parsing failed');
    } finally {
      setParsing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await candidatesAPI.create(formData);
      toast.success('Candidate added');
      setShowForm(false);
      fetchCandidates();
    } catch {
      toast.error('Failed to add candidate');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete candidate?')) return;
    try {
      await candidatesAPI.delete(id);
      toast.success('Candidate deleted');
      fetchCandidates();
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div style={styles.page}>
      <style>{css}</style>

      {/* HEADER */}
      <div className="header">
        <div>
          <h2>👥 Candidate Management</h2>
          <p className="subtitle">Manage and track all candidates</p>
        </div>
        <button className="btn primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? '✕ Cancel' : '✚ Add Candidate'}
        </button>
      </div>

      {/* FORM */}
      {showForm && (
        <form className="card" onSubmit={handleSubmit}>
          <h3>Add New Candidate</h3>

          <label>📄 Upload Resume (PDF)</label>
          <input type="file" accept=".pdf" onChange={handleResumeUpload} />

         <div className="form-grid">
             <input name="firstname" placeholder="First Name" value={formData.first_name} onChange={handleChange} />
             <input name="lastname" placeholder="Last Name" value={formData.last_name} onChange={handleChange} />
             <input name="email" placeholder="Email Address" value={formData.email_id} onChange={handleChange} />
             <input name="phone" placeholder="Phone Number" value={formData.phone_number} onChange={handleChange} />
             <input name="skills" placeholder="Skills (comma separated)" value={formData.skills} onChange={handleChange} />
             <input name="experience" placeholder="Experience (Years)" value={formData.experience} onChange={handleChange} />
            <input name="education" placeholder="Educational Background" value={formData.education} onChange={handleChange} />
           </div>

          <label>Position</label>
          <select name="position" onChange={handleChange} required>
            <option value="">Select Position</option>
            <option>Embedded System</option>
            <option>Embedded Software</option>
            <option>Post Silicon Validation</option>
            <option>VLSI</option>
            <option>HR</option>
            <option>Business Unit</option>
          </select>

          <button className="btn primary" disabled={parsing} style={{marginTop: '20px'}}>
            {parsing ? '⏳ Parsing Resume...' : '💾 Save Candidate'}
          </button>
        </form>
      )}

      {/* TABLE */}
      <div className="card">
        <h3>All Candidates ({candidates.length})</h3>
        {candidates.length === 0 ? (
          <div className="empty-state">
            <p className="empty-state-icon">📭</p>
            <p className="empty-state-text">No candidates found. Add one to get started!</p>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Candidate</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Updated By</th>
                <th align="right">Action</th>
              </tr>
            </thead>
            <tbody>
              {candidates.map((c, idx) => (
                <tr key={c.id} style={{animationDelay: `${idx * 0.05}s`}}>
                  <td className="name">
                    <div className="avatar">{(c.first_name || 'C')[0]}</div>
                    <div>
                      <strong>{c.first_name} {c.last_name}</strong>
                      <div className="muted">ID #{c.id}</div>
                    </div>
                  </td>
                  <td>{c.email_id || '-'}</td>
                  <td>{c.phone_number || '-'}</td>
                  <td>
                    <span className={`status ${c.status}`}>{c.status || 'applied'}</span>
                  </td>
                  <td>{c.updated_by_name || '-'}</td>
                  <td align="right" style={{display: 'flex', gap: '8px', justifyContent: 'flex-end'}}>
                    <button className="btn outline" onClick={() => navigate(`/candidates/${c.id}`)}>👁 View</button>
                    <button className="btn danger" onClick={() => handleDelete(c.id)}>🗑 Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ================= STYLES ================= */

const styles = {
  page: {
    padding: '28px',
    background: 'linear-gradient(135deg, #0F172A, #1E3A8A)',
    minHeight: '100vh',
    fontFamily: "'Inter', sans-serif",
    color: '#0F172A',
    animation: 'fadeInUp 0.6s ease-out',
  }
};

const css = `
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: white;
  margin-bottom: 28px;
  animation: fadeInDown 0.5s ease-out;
  gap: 20px;
  flex-wrap: wrap;
}

.header h2 {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
}

.subtitle {
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  margin: 4px 0 0 0;
}

.card {
  background: white;
  padding: 28px;
  border-radius: 16px;
  margin-bottom: 24px;
  box-shadow: 0 12px 28px rgba(0,0,0,0.15);
  animation: fadeInUp 0.5s ease-out;
  transition: all 0.3s ease;
}

.card:hover {
  box-shadow: 0 15px 35px rgba(0,0,0,0.2);
  transform: translateY(-2px);
}

.card h3 {
  margin-top: 0;
  color: #0F172A;
  font-weight: 700;
}

label {
  display: block;
  margin: 16px 0 8px 0;
  font-weight: 600;
  color: #0F172A;
}

input[type="file"],
input[type="text"],
input[type="email"],
select {
  width: 100%;
  padding: 12px 16px;
  margin-bottom: 16px;
  border: 2px solid #E2E8F0;
  border-radius: 10px;
  font-size: 14px;
  transition: all 0.3s ease;
  font-family: 'Inter', sans-serif;
}

input[type="file"]:hover,
input[type="text"]:hover,
input[type="email"]:hover,
select:hover {
  border-color: #1E40AF;
}

input[type="file"]:focus,
input[type="text"]:focus,
input[type="email"]:focus,
select:focus {
  outline: none;
  border-color: #1E40AF;
  box-shadow: 0 0 0 3px rgba(30, 64, 175, 0.1);
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 0;
}

.form-grid input {
  margin-bottom: 0;
}

.btn {
  padding: 11px 22px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.btn.primary {
  background: linear-gradient(135deg, #1E40AF, #1e3a8a);
  color: white;
  box-shadow: 0 4px 12px rgba(30, 64, 175, 0.3);
}

.btn.primary:hover {
  box-shadow: 0 6px 20px rgba(30, 64, 175, 0.4);
  transform: translateY(-2px);
}

.btn.outline {
  background: transparent;
  color: #1E40AF;
  border: 2px solid #1E40AF;
  padding: 9px 16px;
  font-size: 13px;
}

.btn.outline:hover {
  background: #1E40AF;
  color: white;
  transform: translateY(-2px);
}

.btn.danger {
  background: linear-gradient(135deg, #EF4444, #DC2626);
  color: white;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
  padding: 9px 16px;
  font-size: 13px;
}

.btn.danger:hover {
  box-shadow: 0 6px 20px rgba(239, 68, 68, 0.4);
  transform: translateY(-2px);
}

table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  animation: fadeInUp 0.5s ease-out;
}

thead {
  background: linear-gradient(90deg, #F8FAFC, #F0F4FF);
}

th {
  padding: 16px;
  text-align: left;
  font-weight: 600;
  color: #0F172A;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #E2E8F0;
}

td {
  padding: 16px;
  border-bottom: 1px solid #E2E8F0;
  color: #0F172A;
}

tbody tr {
  transition: all 0.3s ease;
  animation: fadeInUp 0.4s ease-out;
}

tbody tr:hover {
  background: #F8FAFC;
  transform: scale(1.01);
}

.avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1E40AF, #0EA5E9);
  color: white;
  font-weight: 700;
  margin-right: 12px;
  animation: scaleIn 0.3s ease-out;
}

.status {
  display: inline-block;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  animation: scaleIn 0.3s ease-out;
}

.status.applied {
  background: #EFF6FF;
  color: #1E40AF;
}

.status.interview {
  background: #F0FDF4;
  color: #10B981;
}

.status.rejected {
  background: #FEF2F2;
  color: #EF4444;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  animation: fadeInUp 0.5s ease-out;
}

.empty-state-icon {
  font-size: 48px;
  margin-bottom: 12px;
}

.empty-state-text {
  color: #64748B;
  font-size: 16px;
  margin: 0;
}

.muted {
  color: #94A3B8;
  font-size: 12px;
  font-weight: 400;
}

@media (max-width: 768px) {
  .header {
    flex-direction: column;
    align-items: flex-start;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  table {
    font-size: 12px;
  }

  th, td {
    padding: 12px;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
.grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px,1fr));
  gap: 12px;
}

input, select {
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #CBD5E1;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th {
  background: #F1F5F9;
  text-align: left;
  padding: 14px;
}

td {
  padding: 14px;
  border-top: 1px solid #E5E7EB;
}

.name {
  display: flex;
  gap: 12px;
  align-items: center;
}

.avatar {
  background: #1E40AF;
  color: white;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

.btn {
  padding: 8px 14px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-weight: 600;
  margin-left: 6px;
}

.primary { background: #1E40AF; color: white; }
.outline { background: white; border: 1px solid #1E40AF; color: #1E40AF; }
.danger { background: #DC2626; color: white; }

.status {
  padding: 4px 10px;
  border-radius: 14px;
  font-size: 12px;
  font-weight: 600;
}

.status.applied { background: #E5E7EB; }
.status.interview { background: #DBEAFE; color: #1E40AF; }
.status.offered { background: #DCFCE7; color: #166534; }

.muted {
  font-size: 12px;
  color: #64748B;
}
`;

// import React, { useEffect, useState } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { candidatesAPI, aiAPI } from '../api/client';
// import { toast } from 'react-toastify';

// export default function CandidatesList() {
//   const [candidates, setCandidates] = useState([]);
//   const [showForm, setShowForm] = useState(false);
//   const [parsing, setParsing] = useState(false);

//   const [formData, setFormData] = useState({
//     firstname: '',
//     lastname: '',
//     email: '',
//     phone: '',
//     skills: '',
//     experience: '',
//     education: '',
//     position: ''
//   });

//   const navigate = useNavigate();

//   /* ================= FETCH CANDIDATES ================= */

//   useEffect(() => {
//     fetchCandidates();
//   }, []);

//   const fetchCandidates = async () => {
//     console.log('📡 Fetching candidates...');
//     try {
//       const res = await candidatesAPI.getAll();
//       console.log('📡 Candidates response:', res.data);
//       setCandidates(res.data);
//     } catch (err) {
//       console.error('❌ Fetch candidates error:', err);
//       toast.error('Failed to fetch candidates');
//     }
//   };

//   /* ================= FORM DATA WATCH ================= */

//   useEffect(() => {
//     console.log('📝 formData updated:', formData);
//   }, [formData]);

//   /* ================= INPUT CHANGE ================= */

//   const handleChange = (e) => {
//     console.log(
//       '✏️ Field change:',
//       e.target.name,
//       '=>',
//       e.target.value
//     );

//     setFormData({
//       ...formData,
//       [e.target.name]: e.target.value,
//     });
//   };

//   /* ================= RESUME UPLOAD ================= */

//   const handleResumeUpload = async (e) => {
//     const file = e.target.files[0];

//     console.log('📂 Selected file:', file);
//     console.log('📂 File type:', file?.type);
//     console.log('📂 File size (MB):', file?.size / 1024 / 1024);

//     if (!file) return;

//     setParsing(true);

//     const fd = new FormData();
//     fd.append('resume', file);

//     try {
//       console.log('🚀 Sending resume for parsing...');
//       const res = await aiAPI.parseResume(fd);

//       console.log('🧠 Parse resume API response:', res);
//       console.log('🧠 Parsed resume data:', res.data);

//       setFormData(res.data);
//       toast.success('Resume parsed successfully');
//     } catch (err) {
//       console.error('❌ Resume parsing failed:', err);
//       toast.error('Resume parsing failed');
//     } finally {
//       setParsing(false);
//     }
//   };

//   /* ================= SUBMIT ================= */

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     console.log('🚀 Submitting candidate payload:', formData);

//     try {
//       await candidatesAPI.create(formData);
//       toast.success('Candidate added');

//       setShowForm(false);
//       fetchCandidates();
//     } catch (err) {
//       console.error('❌ Create candidate failed:', err);
//       toast.error('Failed to add candidate');
//     }
//   };

//   /* ================= DELETE ================= */

//   const handleDelete = async (id) => {
//     console.log('🗑️ Deleting candidate ID:', id);

//     if (!window.confirm('Delete candidate?')) return;

//     try {
//       await candidatesAPI.delete(id);
//       toast.success('Candidate deleted');
//       fetchCandidates();
//     } catch (err) {
//       console.error('❌ Delete failed:', err);
//       toast.error('Delete failed');
//     }
//   };

//   /* ================= UI ================= */

//   return (
//     <div style={{ padding: '16px' }}>
//       {/* INBUILT CSS */}
//       <style>{`
//         .header {
//           display: flex;
//           justify-content: space-between;
//           align-items: center;
//         }
//         .btn {
//           padding: 8px 14px;
//           border-radius: 6px;
//           border: none;
//           cursor: pointer;
//           font-weight: 500;
//         }
//         .btn-primary {
//           background: #007bff;
//           color: #fff;
//         }
//         .btn-danger {
//           background: #dc3545;
//           color: #fff;
//           margin-left: 6px;
//         }
//         .btn-outline {
//           background: transparent;
//           border: 1px solid #007bff;
//           color: #007bff;
//           margin-right: 6px;
//         }
//         .form-box {
//           background: #f9fafb;
//           padding: 16px;
//           margin-top: 14px;
//           border-radius: 10px;
//         }
//         .form-grid {
//           display: grid;
//           grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
//           gap: 12px;
//           margin-top: 10px;
//         }
//         label {
//           font-size: 13px;
//           font-weight: 600;
//         }
//         input, select {
//           padding: 8px;
//           border-radius: 6px;
//           border: 1px solid #ccc;
//           width: 100%;
//         }
//         table {
//           width: 100%;
//           border-collapse: collapse;
//           margin-top: 16px;
//           background: #fff;
//           border-radius: 10px;
//           overflow: hidden;
//         }
//         thead {
//           background: #f4f6f8;
//         }
//         th, td {
//           padding: 14px;
//           text-align: left;
//           font-size: 14px;
//         }
//         tbody tr {
//           border-top: 1px solid #eee;
//         }
//         tbody tr:hover {
//           background: #f9fbff;
//         }
//         .name-cell {
//           display: flex;
//           align-items: center;
//           gap: 12px;
//         }
//         .avatar {
//           width: 36px;
//           height: 36px;
//           border-radius: 50%;
//           background: #007bff;
//           color: #fff;
//           display: flex;
//           align-items: center;
//           justify-content: center;
//           font-weight: bold;
//         }
//         .sub-text {
//           font-size: 12px;
//           color: #888;
//         }
//         .actions {
//           text-align: right;
//         }
//       `}</style>

//       {/* HEADER */}
//       <div className="header">
//         <h2>Candidates</h2>
//         <button
//           className="btn btn-primary"
//           onClick={() => setShowForm(!showForm)}
//         >
//           {showForm ? 'Cancel' : '+ Add Candidate'}
//         </button>
//       </div>

//       {/* FORM */}
//       {showForm && (
//         <form className="form-box" onSubmit={handleSubmit}>
//           <label>Upload Resume</label>
//           <input type="file" accept=".pdf" onChange={handleResumeUpload} />

//           <div className="form-grid">
//             <input name="firstname" placeholder="First Name" value={formData.first_name} onChange={handleChange} />
//             <input name="lastname" placeholder="Last Name" value={formData.last_name} onChange={handleChange} />
//             <input name="email" placeholder="Email" value={formData.email_id} onChange={handleChange} />
//             <input name="phone" placeholder="Phone" value={formData.phone_number} onChange={handleChange} />
//             <input name="skills" placeholder="Skills" value={formData.skills} onChange={handleChange} />
//             <input name="experience" placeholder="Experience" value={formData.experience} onChange={handleChange} />
//             <input name="education" placeholder="Education" value={formData.education} onChange={handleChange} />
//           </div>

//           <label>Position</label>
//           <select name="position" value={formData.position} onChange={handleChange} required>
//             <option value="">Select Position</option>
//             <option value="Embedded System">Embedded System</option>
//             <option value="Embedded Software">Embedded Software</option>
//             <option value="Post Silicon Validation">Post Silicon Validation</option>
//             <option value="VLSI">VLSI</option>
//             <option value="Others">Others</option>
//             <option value="HR">HR</option>
//             <option value="Business Unit">Business Unit</option>
//           </select>

//           <button type="submit" className="btn btn-primary" disabled={parsing} style={{ marginTop: 14 }}>
//             {parsing ? 'Parsing Resume...' : 'Save Candidate'}
//           </button>
//         </form>
//       )}

//       {/* TABLE */}
//       <table>
//         <thead>
//           <tr>
//             <th>Candidate</th>
//             <th>Email</th>
//             <th>Phone</th>
//             <th>Status</th>
//              <th>Updated By</th>
//             <th className="actions">Action</th>
//           </tr>
//         </thead>
//         <tbody>
//           {candidates.map((c) => (
//             <tr key={c.id}>
//               <td>
//                 <div className="name-cell">
//                   <div className="avatar">{(c.first_name || 'C')[0]}</div>
//                   <div>
//                     <div>{c.first_name} {c.last_name}</div>
//                     <div className="sub-text">ID #{c.id}</div>
//                   </div>
//                 </div>
//               </td>
//               <td>{c.email_id || '-'}</td>
//               <td>{c.phone_number || '-'}</td>
//               <td>{c.status || '-'}</td>
//               <td>{c.updated_by_name || "-"}</td>
//               <td className="actions">
//                 <button className="btn btn-outline" onClick={() => navigate(`/candidates/${c.id}`)}>View</button>
//                 <button className="btn btn-danger" onClick={() => handleDelete(c.id)}>Delete</button>
//               </td>
//             </tr>
//           ))}
//         </tbody>
//       </table>
//     </div>
//   );
// }

// // import React, { useEffect, useState } from 'react';
// // import { useNavigate } from 'react-router-dom';
// // import { candidatesAPI, aiAPI } from '../api/client';
// // import { toast } from 'react-toastify';

// // export default function CandidatesList() {
// //   const [candidates, setCandidates] = useState([]);
// //   const [showForm, setShowForm] = useState(false);
// //   const [parsing, setParsing] = useState(false);

// //   const [formData, setFormData] = useState({
// //     firstname: '',
// //     lastname: '',
// //     email: '',
// //     phone: '',
// //     skills: '',
// //     experience: '',
// //     education: '',
// //     position: ''   // ✅ USE EXISTING COLUMN
// //   });

// //   const navigate = useNavigate();

// //   useEffect(() => {
// //     fetchCandidates();
// //   }, []);

// //   const fetchCandidates = async () => {
// //     try {
// //       const res = await candidatesAPI.getAll();
// //       setCandidates(res.data);
// //     } catch {
// //       toast.error('Failed to fetch candidates');
// //     }
// //   };

// //   const handleChange = (e) => {
// //     setFormData({ ...formData, [e.target.name]: e.target.value });
// //   };

// //   const handleResumeUpload = async (e) => {
// //     const file = e.target.files[0];
// //     if (!file) return;

// //     setParsing(true);
// //     const fd = new FormData();
// //     fd.append('resume', file);

// //     try {
// //       const res = await aiAPI.parseResume(fd);
// //       setFormData(res.data);
// //       toast.success('Resume parsed successfully');
// //     } catch {
// //       toast.error('Resume parsing failed');
// //     } finally {
// //       setParsing(false);
// //     }
// //   };

// //   const handleSubmit = async (e) => {
// //     e.preventDefault();
// //     try {
// //       await candidatesAPI.create(formData);
// //       toast.success('Candidate added');
// //       setShowForm(false);
// //       fetchCandidates();
// //     } catch {
// //       toast.error('Failed to add candidate');
// //     }
// //   };

// //   const handleDelete = async (id) => {
// //     if (!window.confirm('Delete candidate?')) return;
// //     await candidatesAPI.delete(id);
// //     fetchCandidates();
// //   };

// //   return (
// //     <div style={{ padding: '16px' }}>
// //       {/* INBUILT CSS */}
// //       <style>{`
// //         .header {
// //           display: flex;
// //           justify-content: space-between;
// //           align-items: center;
// //         }

// //         .btn {
// //           padding: 8px 14px;
// //           border-radius: 6px;
// //           border: none;
// //           cursor: pointer;
// //           font-weight: 500;
// //         }

// //         .btn-primary {
// //           background: #007bff;
// //           color: #fff;
// //         }

// //         .btn-danger {
// //           background: #dc3545;
// //           color: #fff;
// //           margin-left: 6px;
// //         }

// //         .btn-outline {
// //           background: transparent;
// //           border: 1px solid #007bff;
// //           color: #007bff;
// //           margin-right: 6px;
// //         }

// //         .form-box {
// //           background: #f9fafb;
// //           padding: 16px;
// //           margin-top: 14px;
// //           border-radius: 10px;
// //         }

// //         .form-grid {
// //           display: grid;
// //           grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
// //           gap: 12px;
// //           margin-top: 10px;
// //         }

// //         label {
// //           font-size: 13px;
// //           font-weight: 600;
// //         }

// //         input {
// //           padding: 8px;
// //           border-radius: 6px;
// //           border: 1px solid #ccc;
// //           width: 100%;
// //         }

// //         table {
// //           width: 100%;
// //           border-collapse: collapse;
// //           margin-top: 16px;
// //           background: #fff;
// //           border-radius: 10px;
// //           overflow: hidden;
// //         }

// //         thead {
// //           background: #f4f6f8;
// //         }

// //         th, td {
// //           padding: 14px;
// //           text-align: left;
// //           font-size: 14px;
// //         }

// //         tbody tr {
// //           border-top: 1px solid #eee;
// //         }

// //         tbody tr:hover {
// //           background: #f9fbff;
// //         }

// //         .name-cell {
// //           display: flex;
// //           align-items: center;
// //           gap: 12px;
// //         }

// //         .avatar {
// //           width: 36px;
// //           height: 36px;
// //           border-radius: 50%;
// //           background: #007bff;
// //           color: #fff;
// //           display: flex;
// //           align-items: center;
// //           justify-content: center;
// //           font-weight: bold;
// //         }

// //         .sub-text {
// //           font-size: 12px;
// //           color: #888;
// //         }

// //         .actions {
// //           text-align: right;
// //         }
// //       `}</style>

// //       {/* HEADER */}
// //       <div className="header">
// //         <h2>Candidates</h2>
// //         <button
// //           className="btn btn-primary"
// //           onClick={() => setShowForm(!showForm)}
// //         >
// //           {showForm ? 'Cancel' : '+ Add Candidate'}
// //         </button>
// //       </div>

// //       {/* FORM */}
// //       {showForm && (
// //         <form className="form-box" onSubmit={handleSubmit}>
// //           <label>Upload Resume</label>
// //           <input type="file" accept=".pdf" onChange={handleResumeUpload} />

// //           <div className="form-grid">
// //             <div>
// //               <label>First Name</label>
// //               <input name="firstname" value={formData.first_name} onChange={handleChange} />
// //             </div>

// //             <div>
// //               <label>Last Name</label>
// //               <input name="lastname" value={formData.last_name} onChange={handleChange} />
// //             </div>

// //             <div>
// //               <label>Email</label>
// //               <input name="email" value={formData.email_id} onChange={handleChange} />
// //             </div>

// //             <div>
// //               <label>Phone</label>
// //               <input name="phone" value={formData.phone_number} onChange={handleChange} />
// //             </div>

// //             <div>
// //               <label>Skills</label>
// //               <input name="skills" value={formData.skills} onChange={handleChange} />
// //             </div>

// //             <div>
// //               <label>Experience</label>
// //               <input name="experience" value={formData.experience} onChange={handleChange} />
// //             </div>

// //             <div>
// //               <label>Education</label>
// //               <input name="education" value={formData.education} onChange={handleChange} />
// //             </div>
// //           </div>

// //                       <div>
// //               <label>Position</label>
// //               <select
// //                 name="position"
// //                 value={formData.position}
// //                 onChange={handleChange}
// //                 required
// //               >
// //                 <option value="">Select Position</option>
// //                 <option value="Embedded System">Embedded System</option>
// //                 <option value="Embedded Software">Embedded Software</option>
// //                 <option value="Post Silicon Validation">Post Silicon Validation</option>
// //                 <option value="VLSI">VLSI</option>
// //                 <option value="Others">Others</option>
// //                 <option value="HR">HR</option>
// //                 <option value="Business Unit">Business Unit</option>
// //               </select>
// //             </div>


// //           <button
// //             type="submit"
// //             className="btn btn-primary"
// //             style={{ marginTop: '14px' }}
// //             disabled={parsing}
// //           >
// //             {parsing ? 'Parsing Resume...' : 'Save Candidate'}
// //           </button>
// //         </form>
// //       )}

// //       {/* TABLE */}
// //       <table>
// //         <thead>
// //           <tr>
// //             <th>Candidate</th>
// //             <th>Email</th>
// //             <th>Phone</th>
// //             <th className="actions">Action</th>
// //           </tr>
// //         </thead>
// //         <tbody>
// //           {candidates.map((c) => (
// //             <tr key={c.id}>
// //               <td>
// //                 <div className="name-cell">
// //                   <div className="avatar">
// //                     {(c.first_name || 'C')[0]}
// //                   </div>
// //                   <div>
// //                     <div>{c.first_name} {c.last_name}</div>
// //                     <div className="sub-text">ID #{c.id}</div>
// //                   </div>
// //                 </div>
// //               </td>

// //               <td>{c.email_id || '-'}</td>
// //               <td>{c.phone_number || '-'}</td>

// //               <td className="actions">
// //                 <button
// //                   className="btn btn-outline"
// //                   onClick={() => navigate(`/candidates/${c.id}`)}
// //                 >
// //                   View
// //                 </button>
// //                 <button
// //                   className="btn btn-danger"
// //                   onClick={() => handleDelete(c.id)}
// //                 >
// //                   Delete
// //                 </button>
// //               </td>
// //             </tr>
// //           ))}
// //         </tbody>
// //       </table>
// //     </div>
// //   );
// // }


// // // import React, { useEffect, useState } from 'react';
// // // import { useNavigate } from 'react-router-dom';
// // // import { candidatesAPI, aiAPI } from '../api/client';
// // // import { toast } from 'react-toastify';

// // // export default function CandidatesList() {
// // //   const [candidates, setCandidates] = useState([]);
// // //   const [showForm, setShowForm] = useState(false);
// // //   const [parsing, setParsing] = useState(false);

// // //   const [formData, setFormData] = useState({
// // //     firstname: '',
// // //     lastname: '',
// // //     email: '',
// // //     phone: '',
// // //     skills: '',
// // //     education: '',
// // //     experience: '',
// // //   });

// // //   const navigate = useNavigate();

// // //   useEffect(() => {
// // //     fetchCandidates();
// // //   }, []);

// // //   const fetchCandidates = async () => {
// // //     try {
// // //       const res = await candidatesAPI.getAll();
// // //       setCandidates(res.data);
// // //     } catch {
// // //       toast.error('Failed to fetch candidates');
// // //     }
// // //   };

// // //   const handleChange = (e) => {
// // //     setFormData({ ...formData, [e.target.name]: e.target.value });
// // //   };

// // //   const handleResumeUpload = async (e) => {
// // //     const file = e.target.files[0];
// // //     if (!file) return;

// // //     setParsing(true);
// // //     const fd = new FormData();
// // //     fd.append('resume', file);

// // //     try {
// // //       const res = await aiAPI.parseResume(fd);
// // //       setFormData(res.data);
// // //       toast.success('Resume parsed successfully');
// // //     } catch {
// // //       toast.error('Resume parsing failed');
// // //     } finally {
// // //       setParsing(false);
// // //     }
// // //   };

// // //   const handleSubmit = async (e) => {
// // //     e.preventDefault();
// // //     try {
// // //       await candidatesAPI.create(formData);
// // //       toast.success('Candidate added');
// // //       setShowForm(false);
// // //       fetchCandidates();
// // //     } catch {
// // //       toast.error('Failed to add candidate');
// // //     }
// // //   };

// // //   const handleDelete = async (id) => {
// // //     if (!window.confirm('Delete candidate?')) return;
// // //     await candidatesAPI.delete(id);
// // //     fetchCandidates();
// // //   };

// // //   return (
// // //     <div className="card">
// // //       <h2>Candidates</h2>

// // //       <button onClick={() => setShowForm(!showForm)}>
// // //         {showForm ? 'Cancel' : 'Add Candidate'}
// // //       </button>

// // //       {showForm && (
// // //         <form onSubmit={handleSubmit}>
// // //           <input type="file" accept=".pdf" onChange={handleResumeUpload} />
// // //         <text> First Name </text>
// // //           <input name="firstname" placeholder="First Name *"  value={formData.first_name} onChange={handleChange} />
// // //           <text> Last Name </text>
// // //           <input name="lastname" placeholder="Last Name" value={formData.last_name} onChange={handleChange} />
// // //           <text> Email </text>
// // //           <input name="email" placeholder="Email" value={formData.email_id} onChange={handleChange} />
// // //           <text> Phone No. </text>
// // //           <input name="phone" placeholder="Phone" value={formData.phone_number} onChange={handleChange} />
// // //           <text> Skills </text>
// // //           <input name="skills" placeholder="Skills" value={formData.skills} onChange={handleChange} />
// // //           <text> Experience </text>
// // //           <input name="Experience" placeholder="Experience" value={formData.experience} onChange={handleChange} />
// // //           <text> Education </text>
// // //           <input name="Education" placeholder="Education" value={formData.education} onChange={handleChange} />
// // //           <br></br>
// // //           <button disabled={parsing}>
// // //             {parsing ? 'Parsing...' : 'Save'}
// // //           </button>
// // //         </form>
// // //       )}

// // //       <table className="table">
// // //         <thead>
// // //           <tr>
// // //             <th>Name</th>
// // //             <th>Email</th>
// // //             <th>Phone</th>
// // //             <th>Action</th>
// // //           </tr>
// // //         </thead>
// // //         <tbody>
// // //           {candidates.map(c => (
// // //             <tr key={c.id}>
// // //               <td>{c.first_name} {c.last_name}</td>
// // //               <td>{c.email_id || '-'}</td>
// // //               <td>{c.phone_number || '-'}</td>
// // //               <td>
// // //                 <button onClick={() => navigate(`/candidates/${c.id}`)}>View</button>
// // //                 <button onClick={() => handleDelete(c.id)}>Delete</button>
// // //               </td>
// // //             </tr>
// // //           ))}
// // //         </tbody>
// // //       </table>
// // //     </div>
// // //   );
// // // }

// // // // import React, { useEffect, useRef, useState } from 'react';
// // // // import { useNavigate } from 'react-router-dom';
// // // // import { candidatesAPI, aiAPI } from '../api/client';
// // // // import { toast } from 'react-toastify';

// // // // export default function CandidatesList() {
// // // //   const [candidates, setCandidates] = useState([]);
// // // //   const [loading, setLoading] = useState(true);
// // // //   const [showForm, setShowForm] = useState(false);
// // // //   const [parsing, setParsing] = useState(false);

// // // //   // 🔥 INPUT REFS
// // // //   const firstNameRef = useRef(null);
// // // //   const lastNameRef = useRef(null);
// // // //   const emailRef = useRef(null);
// // // //   const phoneRef = useRef(null);
// // // //   const skillsRef = useRef(null);

// // // //   const [formData, setFormData] = useState({
// // // //     firstname: '',
// // // //     lastname: '',
// // // //     email: '',
// // // //     phone: '',
// // // //     skills: '',
// // // //     resume: null, // only for parsing, NOT for submit
// // // //   });

// // // //   const [resumeFileName, setResumeFileName] = useState('');
// // // //   const navigate = useNavigate();

// // // //   /* ================= FETCH CANDIDATES ================= */

// // // //   useEffect(() => {
// // // //     fetchCandidates();
// // // //   }, []);

// // // //   const fetchCandidates = async () => {
// // // //     try {
// // // //       const res = await candidatesAPI.getAll();
// // // //       setCandidates(res.data);
// // // //     } catch {
// // // //       toast.error('Failed to fetch candidates');
// // // //     } finally {
// // // //       setLoading(false);
// // // //     }
// // // //   };

// // // //   /* ================= INPUT CHANGE ================= */

// // // //   const handleInputChange = (e) => {
// // // //     const { name, value } = e.target;
// // // //     setFormData((prev) => ({ ...prev, [name]: value }));
// // // //   };

// // // //   /* ================= RESUME PARSE ================= */

// // // //   const handleResumeUpload = async (e) => {
// // // //     const file = e.target.files[0];
// // // //     if (!file) return;

// // // //     setResumeFileName(file.name);
// // // //     setParsing(true);

// // // //     const fd = new FormData();
// // // //     fd.append('resume', file);

// // // //     try {
// // // //       const res = await aiAPI.parseResume(fd);
// // // //       const data = res.data?.data || res.data;

// // // //       // Save parsed data into state
// // // //       setFormData((prev) => ({
// // // //         ...prev,
// // // //         first_name: data.firstName || '',
// // // //         last_name: data.lastName || '',
// // // //         email_id: data.email || '',
// // // //         phone_number: data.phone || '',
// // // //         skills: data.skills || '',
// // // //         resume: file,
// // // //       }));

// // // //       // Force values into inputs (uncontrolled inputs)
// // // //       setTimeout(() => {
// // // //         if (firstNameRef.current) firstNameRef.current.value = data.firstName || '';
// // // //         if (lastNameRef.current) lastNameRef.current.value = data.lastName || '';
// // // //         if (emailRef.current) emailRef.current.value = data.email || '';
// // // //         if (phoneRef.current) phoneRef.current.value = data.phone || '';
// // // //         if (skillsRef.current) skillsRef.current.value = data.skills || '';
// // // //       }, 0);

// // // //       toast.success('Resume parsed & auto-filled');
// // // //     } catch (err) {
// // // //       console.error(err);
// // // //       toast.error('Resume parsing failed');
// // // //     } finally {
// // // //       setParsing(false);
// // // //     }
// // // //   };

// // // //   /* ================= SUBMIT CANDIDATE ================= */

// // // //   const handleSubmit = async (e) => {
// // // //     e.preventDefault();

// // // //     try {
// // // //       const fd = new FormData();

// // // //       // 🚨 IMPORTANT: DO NOT SEND resume file again
// // // //       Object.entries(formData).forEach(([key, value]) => {
// // // //         if (value && key !== 'resume') {
// // // //           fd.append(key, value);
// // // //         }
// // // //       });

// // // //       await candidatesAPI.create(fd);

// // // //       toast.success('Candidate added');
// // // //       setShowForm(false);
// // // //       setResumeFileName('');
// // // //       fetchCandidates();
// // // //     } catch (err) {
// // // //       console.error(err);
// // // //       toast.error('Failed to create candidate');
// // // //     }
// // // //   };

// // // //   /* ================= DELETE ================= */

// // // //   const handleDelete = async (id) => {
// // // //     if (!window.confirm('Are you sure you want to delete this candidate?')) return;

// // // //     try {
// // // //       await candidatesAPI.delete(id);
// // // //       toast.success('Candidate deleted');
// // // //       fetchCandidates();
// // // //     } catch {
// // // //       toast.error('Failed to delete candidate');
// // // //     }
// // // //   };

// // // //   /* ================= UI ================= */

// // // //   return (
// // // //     <div className="card">
// // // //       <div className="flex-between">
// // // //         <h2>Candidates</h2>
// // // //         <button onClick={() => setShowForm(!showForm)}>
// // // //           {showForm ? 'Cancel' : 'Add Candidate'}
// // // //         </button>
// // // //       </div>

// // // //       {showForm && (
// // // //         <form onSubmit={handleSubmit} autoComplete="off">
// // // //           <label>Resume Upload</label>
// // // //           <input
// // // //             type="file"
// // // //             accept=".pdf,.doc,.docx"
// // // //             onChange={handleResumeUpload}
// // // //           />
// // // //           {resumeFileName && <p>Selected: {resumeFileName}</p>}

// // // //           <input
// // // //             ref={firstNameRef}
// // // //             name="firstname"
// // // //             placeholder="First Name"
// // // //             onChange={handleInputChange}
// // // //             required
// // // //           />

// // // //           <input
// // // //             ref={lastNameRef}
// // // //             name="lastname"
// // // //             placeholder="Last Name"
// // // //             onChange={handleInputChange}
// // // //             required
// // // //           />

// // // //           <input
// // // //             ref={emailRef}
// // // //             name="email"
// // // //             placeholder="Email"
// // // //             onChange={handleInputChange}
// // // //           />

// // // //           <input
// // // //             ref={phoneRef}
// // // //             name="phone"
// // // //             placeholder="Phone"
// // // //             onChange={handleInputChange}
// // // //           />

// // // //           <input
// // // //             ref={skillsRef}
// // // //             name="skills"
// // // //             placeholder="Skills"
// // // //             onChange={handleInputChange}
// // // //           />

// // // //           <button type="submit" disabled={parsing}>
// // // //             {parsing ? 'Parsing Resume...' : 'Add Candidate'}
// // // //           </button>
// // // //         </form>
// // // //       )}

// // // //       {!loading && (
// // // //         <table className="table">
// // // //           <thead>
// // // //             <tr>
// // // //               <th>firstName</th>
// // // //               <th>LastName</th>
// // // //               <th>Email</th>
// // // //               <th>Phone</th>
// // // //               <th>Skills</th>
// // // //               <th>Actions</th>
// // // //             </tr>
// // // //           </thead>
// // // //           <tbody>
// // // //             {candidates.map((c) => (
// // // //               <tr key={c.id}>
// // // //                 <td>{c.first_name}</td>
// // // //                 <td>{c.last_name}</td>
// // // //                 <td>{c.email_id || '-'}</td>
// // // //                 <td>{c.phone_number || '-'}</td>
// // // //                 <td className="ellipsis">{c.skills || '-'}</td>
// // // //                 <td>
// // // //                   <button onClick={() => navigate(`/candidates/${c.id}`)}>
// // // //                     View
// // // //                   </button>
// // // //                   <button
// // // //                     className="danger"
// // // //                     onClick={() => handleDelete(c.id)}
// // // //                   >
// // // //                     Delete
// // // //                   </button>
// // // //                 </td>
// // // //               </tr>
// // // //             ))}
// // // //           </tbody>
// // // //         </table>
// // // //       )}
// // // //     </div>
// // // //   );
// // // // }

// // // // // import React, { useEffect, useRef, useState } from 'react';
// // // // // import { useNavigate } from 'react-router-dom';
// // // // // import { candidatesAPI, aiAPI } from '../api/client';
// // // // // import { toast } from 'react-toastify';

// // // // // export default function CandidatesList() {
// // // // //   const [candidates, setCandidates] = useState([]);
// // // // //   const [loading, setLoading] = useState(true);
// // // // //   const [showForm, setShowForm] = useState(false);
// // // // //   const [parsing, setParsing] = useState(false);

// // // // //   const firstNameRef = useRef(null);
// // // // //   const lastNameRef = useRef(null);
// // // // //   const emailRef = useRef(null);
// // // // //   const phoneRef = useRef(null);
// // // // //   const skillsRef = useRef(null);

// // // // //   const [formData, setFormData] = useState({
// // // // //     first_name: '',
// // // // //     last_name: '',
// // // // //     email_id: '',
// // // // //     phone_number: '',
// // // // //     skills: '',
// // // // //     resume: null,
// // // // //   });

// // // // //   const [resumeFileName, setResumeFileName] = useState('');
// // // // //   const navigate = useNavigate();

// // // // //   /* ================= FETCH ================= */

// // // // //   useEffect(() => {
// // // // //     fetchCandidates();
// // // // //   }, []);

// // // // //   const fetchCandidates = async () => {
// // // // //     try {
// // // // //       const res = await candidatesAPI.getAll();
// // // // //       setCandidates(res.data);
// // // // //     } catch {
// // // // //       toast.error('Failed to fetch candidates');
// // // // //     } finally {
// // // // //       setLoading(false);
// // // // //     }
// // // // //   };

// // // // //   /* ================= INPUT ================= */

// // // // //   const handleInputChange = (e) => {
// // // // //     const { name, value } = e.target;
// // // // //     setFormData((prev) => ({ ...prev, [name]: value }));
// // // // //   };

// // // // //   /* ================= RESUME PARSE ================= */

// // // // //   const handleResumeUpload = async (e) => {
// // // // //     const file = e.target.files[0];
// // // // //     if (!file) return;

// // // // //     setResumeFileName(file.name);
// // // // //     setParsing(true);

// // // // //     const fd = new FormData();
// // // // //     fd.append('resume', file);

// // // // //     try {
// // // // //       const res = await aiAPI.parseResume(fd);
// // // // //       const data = res.data?.data || res.data;

// // // // //       setFormData((prev) => ({
// // // // //         ...prev,
// // // // //         first_name: data.firstName || '',
// // // // //         last_name: data.lastName || '',
// // // // //         email_id: data.email || '',
// // // // //         phone_number: data.phone || '',
// // // // //         skills: data.skills || '',
// // // // //         resume: file,
// // // // //       }));

// // // // //       setTimeout(() => {
// // // // //         firstNameRef.current.value = data.firstName || '';
// // // // //         lastNameRef.current.value = data.lastName || '';
// // // // //         emailRef.current.value = data.email || '';
// // // // //         phoneRef.current.value = data.phone || '';
// // // // //         skillsRef.current.value = data.skills || '';
// // // // //       }, 0);

// // // // //       toast.success('Resume parsed & auto-filled');
// // // // //     } catch {
// // // // //       toast.error('Resume parsing failed');
// // // // //     } finally {
// // // // //       setParsing(false);
// // // // //     }
// // // // //   };

// // // // //   /* ================= SUBMIT ================= */

// // // // //   const handleSubmit = async (e) => {
// // // // //     e.preventDefault();

// // // // //     try {
// // // // //       const fd = new FormData();
// // // // //               Object.entries(formData).forEach(([k, v]) => {
// // // // //               -  if (v) fd.append(k, v);
// // // // //               +  if (v && k !== 'resume') fd.append(k, v);
// // // // //               });
// // // // //       await candidatesAPI.create(fd);
// // // // //       toast.success('Candidate added');
// // // // //       setShowForm(false);
// // // // //       setResumeFileName('');
// // // // //       fetchCandidates();
// // // // //     } catch {
// // // // //       toast.error('Failed to create candidate');
// // // // //     }
// // // // //   };

// // // // //   /* ================= DELETE ================= */

// // // // //   const handleDelete = async (id) => {
// // // // //     if (!window.confirm('Are you sure you want to delete this candidate?')) return;

// // // // //     try {
// // // // //       await candidatesAPI.delete(id);
// // // // //       toast.success('Candidate deleted');
// // // // //       fetchCandidates();
// // // // //     } catch {
// // // // //       toast.error('Failed to delete candidate');
// // // // //     }
// // // // //   };

// // // // //   /* ================= UI ================= */

// // // // //   return (
// // // // //     <div className="card">
// // // // //       <div className="flex-between">
// // // // //         <h2>Candidates</h2>
// // // // //         <button onClick={() => setShowForm(!showForm)}>
// // // // //           {showForm ? 'Cancel' : 'Add Candidate'}
// // // // //         </button>
// // // // //       </div>

// // // // //       {showForm && (
// // // // //         <form onSubmit={handleSubmit}>
// // // // //           <label>Resume Upload</label>
// // // // //           <input type="file" accept=".pdf,.doc,.docx" onChange={handleResumeUpload} />
// // // // //           {resumeFileName && <p>Selected: {resumeFileName}</p>}

// // // // //           <input ref={firstNameRef} name="first_name" placeholder="First Name" onChange={handleInputChange} required />
// // // // //           <input ref={lastNameRef} name="last_name" placeholder="Last Name" onChange={handleInputChange} required />
// // // // //           <input ref={emailRef} name="email_id" placeholder="Email" onChange={handleInputChange} />
// // // // //           <input ref={phoneRef} name="phone_number" placeholder="Phone" onChange={handleInputChange} />
// // // // //           <input ref={skillsRef} name="skills" placeholder="Skills" onChange={handleInputChange} />

// // // // //           <button type="submit" disabled={parsing}>
// // // // //             {parsing ? 'Parsing Resume...' : 'Add Candidate'}
// // // // //           </button>
// // // // //         </form>
// // // // //       )}

// // // // //       {!loading && (
// // // // //         <table className="table">
// // // // //           <thead>
// // // // //             <tr>
// // // // //               <th>Name</th>
// // // // //               <th>Email</th>
// // // // //               <th>Phone</th>
// // // // //               <th>Skills</th>
// // // // //               <th>Actions</th>
// // // // //             </tr>
// // // // //           </thead>
// // // // //           <tbody>
// // // // //             {candidates.map((c) => (
// // // // //               <tr key={c.id}>
// // // // //                 <td>{c.first_name} {c.last_name}</td>
// // // // //                 <td>{c.email_id}</td>
// // // // //                 <td>{c.phone_number}</td>
// // // // //                 <td className="ellipsis">{c.skills || '-'}</td>
// // // // //                 <td>
// // // // //                   <button onClick={() => navigate(`/candidates/${c.id}`)}>View</button>
// // // // //                   <button className="danger" onClick={() => handleDelete(c.id)}>Delete</button>
// // // // //                 </td>
// // // // //               </tr>
// // // // //             ))}
// // // // //           </tbody>
// // // // //         </table>
// // // // //       )}
// // // // //     </div>
// // // // //   );
// // // // // }

// // // // // // import React, { useEffect, useRef, useState } from 'react';
// // // // // // import { useNavigate } from 'react-router-dom';
// // // // // // import { candidatesAPI, aiAPI } from '../api/client';
// // // // // // import { toast } from 'react-toastify';

// // // // // // export default function CandidatesList() {
// // // // // //   const [candidates, setCandidates] = useState([]);
// // // // // //   const [loading, setLoading] = useState(true);
// // // // // //   const [showForm, setShowForm] = useState(false);
// // // // // //   const [parsing, setParsing] = useState(false);

// // // // // //   // 🔥 REFS (DOM CONTROL)
// // // // // //   const firstNameRef = useRef(null);
// // // // // //   const lastNameRef = useRef(null);
// // // // // //   const emailRef = useRef(null);
// // // // // //   const phoneRef = useRef(null);
// // // // // //   const skillsRef = useRef(null);

// // // // // //   const [formData, setFormData] = useState({
// // // // // //     first_name: '',
// // // // // //     last_name: '',
// // // // // //     email_id: '',
// // // // // //     phone_number: '',
// // // // // //     // skills: '',
// // // // // //     resume: null,
// // // // // //   });

// // // // // //   const [resumeFileName, setResumeFileName] = useState('');
// // // // // //   const navigate = useNavigate();

// // // // // //   /* ================= FETCH ================= */

// // // // // //   useEffect(() => {
// // // // // //     fetchCandidates();
// // // // // //   }, []);

// // // // // //   const fetchCandidates = async () => {
// // // // // //     try {
// // // // // //       const res = await candidatesAPI.getAll();
// // // // // //       setCandidates(res.data);
// // // // // //     } catch {
// // // // // //       toast.error('Failed to fetch candidates');
// // // // // //     } finally {
// // // // // //       setLoading(false);
// // // // // //     }
// // // // // //   };

// // // // // //   /* ================= INPUT CHANGE ================= */

// // // // // //   const handleInputChange = (e) => {
// // // // // //     const { name, value } = e.target;
// // // // // //     setFormData((prev) => ({ ...prev, [name]: value }));
// // // // // //   };

// // // // // //   /* ================= RESUME PARSE ================= */

// // // // // //   const handleResumeUpload = async (e) => {
// // // // // //     const file = e.target.files[0];
// // // // // //     if (!file) return;

// // // // // //     setResumeFileName(file.name);
// // // // // //     setParsing(true);

// // // // // //     const fd = new FormData();
// // // // // //     fd.append('resume', file);

// // // // // //     try {
// // // // // //       const res = await aiAPI.parseResume(fd);
// // // // // //       const data = res.data?.data || res.data;

// // // // // //       console.log('PARSED DATA:', data);

// // // // // //       // ✅ UPDATE STATE (for submit)
// // // // // //       setFormData((prev) => ({
// // // // // //         ...prev,
// // // // // //         first_name: data.firstName || '',
// // // // // //         last_name: data.lastName || '',
// // // // // //         email_id: data.email || '',
// // // // // //         phone_number: data.phone || '',
// // // // // //         // skills: data.skills || '',
// // // // // //         resume: file,
// // // // // //       }));

// // // // // //       // 🔥 FORCE VALUES INTO INPUT DOM (THIS IS THE FIX)
// // // // // //       setTimeout(() => {
// // // // // //         if (firstNameRef.current) firstNameRef.current.value = data.firstName || '';
// // // // // //         if (lastNameRef.current) lastNameRef.current.value = data.lastName || '';
// // // // // //         if (emailRef.current) emailRef.current.value = data.email || '';
// // // // // //         if (phoneRef.current) phoneRef.current.value = data.phone || '';
// // // // // //         // if (skillsRef.current) skillsRef.current.value = data.skills || '';
// // // // // //       }, 0);

// // // // // //       toast.success('Resume parsed & auto-filled');
// // // // // //     } catch (err) {
// // // // // //       console.error(err);
// // // // // //       toast.error('Resume parsing failed');
// // // // // //     } finally {
// // // // // //       setParsing(false);
// // // // // //     }
// // // // // //   };

// // // // // //   /* ================= SUBMIT ================= */

// // // // // //   const handleSubmit = async (e) => {
// // // // // //     e.preventDefault();

// // // // // //     try {
// // // // // //       const fd = new FormData();
// // // // // //       Object.entries(formData).forEach(([k, v]) => {
// // // // // //         if (v) fd.append(k, v);
// // // // // //       });

// // // // // //       await candidatesAPI.create(fd);
// // // // // //       toast.success('Candidate added');
// // // // // //       setShowForm(false);
// // // // // //       setResumeFileName('');
// // // // // //       fetchCandidates();
// // // // // //     } catch {
// // // // // //       toast.error('Failed to create candidate');
// // // // // //     }
// // // // // //   };

// // // // // //   /* ================= UI ================= */

// // // // // //   return (
// // // // // //     <div className="card">
// // // // // //       <div className="flex-between">
// // // // // //         <h2>Candidates</h2>
// // // // // //         <button onClick={() => setShowForm(!showForm)}>
// // // // // //           {showForm ? 'Cancel' : 'Add Candidate'}
// // // // // //         </button>
// // // // // //       </div>

// // // // // //       {showForm && (
// // // // // //         <form onSubmit={handleSubmit} autoComplete="off">
// // // // // //           <label>Resume Upload</label>
// // // // // //           <input
// // // // // //             type="file"
// // // // // //             accept=".pdf,.doc,.docx"
// // // // // //             onChange={handleResumeUpload}
// // // // // //           />

// // // // // //           {resumeFileName && <p>Selected: {resumeFileName}</p>}

// // // // // //           {/* 🔥 UNCONTROLLED INPUTS */}
// // // // // //           <input
// // // // // //             ref={firstNameRef}
// // // // // //             name="first_name"
// // // // // //             placeholder="First Name"
// // // // // //             onChange={handleInputChange}
// // // // // //             required
// // // // // //           />

// // // // // //           <input
// // // // // //             ref={lastNameRef}
// // // // // //             name="last_name"
// // // // // //             placeholder="Last Name"
// // // // // //             onChange={handleInputChange}
// // // // // //             required
// // // // // //           />

// // // // // //           <input
// // // // // //             ref={emailRef}
// // // // // //             name="email_id"
// // // // // //             placeholder="Email"
// // // // // //             onChange={handleInputChange}
// // // // // //           />

// // // // // //           <input
// // // // // //             ref={phoneRef}
// // // // // //             name="phone_number"
// // // // // //             placeholder="Phone"
// // // // // //             onChange={handleInputChange}
// // // // // //           />

// // // // // //           <input
// // // // // //             ref={skillsRef}
// // // // // //             name="skills"
// // // // // //             placeholder="Skills"
// // // // // //             onChange={handleInputChange}
// // // // // //           />

// // // // // //           <button type="submit" disabled={parsing}>
// // // // // //             {parsing ? 'Parsing Resume...' : 'Add Candidate'}
// // // // // //           </button>
// // // // // //         </form>
// // // // // //       )}

// // // // // //       {!loading && (
// // // // // //         <table>
// // // // // //           <thead>
// // // // // //             <tr>
// // // // // //               <th>Name</th>
// // // // // //               <th>Email</th>
// // // // // //               <th>Phone</th>
// // // // // //               <th>Action</th>
// // // // // //             </tr>
// // // // // //           </thead>
// // // // // //           <tbody>
// // // // // //             {candidates.map((c) => (
// // // // // //               <tr key={c.id}>
// // // // // //                 <td>{c.first_name} {c.last_name}</td>
// // // // // //                 <td>{c.email_id}</td>
// // // // // //                 <td>{c.phone_number}</td>
// // // // // //                 <td>
// // // // // //                   <button onClick={() => navigate(`/candidates/${c.id}`)}>
// // // // // //                     View
// // // // // //                   </button>
// // // // // //                 </td>
// // // // // //               </tr>
// // // // // //             ))}
// // // // // //           </tbody>
// // // // // //         </table>
// // // // // //       )}
// // // // // //     </div>
// // // // // //   );
// // // // // // }

// // // // // // // import React, { useEffect, useState } from 'react';
// // // // // // // import { useNavigate } from 'react-router-dom';
// // // // // // // import { candidatesAPI, aiAPI } from '../api/client';
// // // // // // // import { toast } from 'react-toastify';

// // // // // // // export default function CandidatesList() {
// // // // // // //   const [candidates, setCandidates] = useState([]);
// // // // // // //   const [loading, setLoading] = useState(true);
// // // // // // //   const [showForm, setShowForm] = useState(false);
// // // // // // //   const [parsing, setParsing] = useState(false);

// // // // // // //   const [formData, setFormData] = useState({
// // // // // // //     first_name: '',
// // // // // // //     last_name: '',
// // // // // // //     email_id: '',
// // // // // // //     phone_number: '',
// // // // // // //     position: '',
// // // // // // //     skills: '',
// // // // // // //     education: '',
// // // // // // //     custom_current_employer: '',
// // // // // // //     custom_overall_experience_years: '',
// // // // // // //     custom_relevant_experience_years: '',
// // // // // // //     custom_current_salary_lpa: '',
// // // // // // //     custom_expected_salary_lpa: '',
// // // // // // //     notice_period: '',
// // // // // // //     resume: null,
// // // // // // //   });

// // // // // // //   const [resumeFileName, setResumeFileName] = useState('');
// // // // // // //   const navigate = useNavigate();

// // // // // // //   /* ================= FETCH CANDIDATES ================= */

// // // // // // //   useEffect(() => {
// // // // // // //     fetchCandidates();
// // // // // // //   }, []);

// // // // // // //   const fetchCandidates = async () => {
// // // // // // //     try {
// // // // // // //       const res = await candidatesAPI.getAll();
// // // // // // //       setCandidates(res.data);
// // // // // // //     } catch {
// // // // // // //       toast.error('Failed to fetch candidates');
// // // // // // //     } finally {
// // // // // // //       setLoading(false);
// // // // // // //     }
// // // // // // //   };

// // // // // // //   /* ================= INPUT HANDLER ================= */

// // // // // // //   const handleInputChange = (e) => {
// // // // // // //     const { name, value } = e.target;
// // // // // // //     setFormData((prev) => ({ ...prev, [name]: value }));
// // // // // // //   };

// // // // // // //   /* ================= RESUME AUTO-FILL ================= */

// // // // // // //  const handleResumeUpload = async (e) => {
// // // // // // //   const file = e.target.files[0];
// // // // // // //   if (!file) return;

// // // // // // //   setResumeFileName(file.name);
// // // // // // //   setParsing(true);

// // // // // // //   const fd = new FormData();
// // // // // // //   fd.append('resume', file);

// // // // // // //   try {
// // // // // // //     const res = await aiAPI.parseResume(fd);

// // // // // // //     // 🔥 FIX HERE
// // // // // // //     const data = res.data?.data || res.data;

// // // // // // //     console.log("Parsed Resume Normalized:", data);

// // // // // // //     setFormData((prev) => ({
// // // // // // //       ...prev,
// // // // // // //       first_name: data.firstName || '',
// // // // // // //       last_name: data.lastName || '',
// // // // // // //       email_id: data.email || '',
// // // // // // //       phone_number: data.phone || '',
// // // // // // //       skills: data.skills || '',
// // // // // // //       education: data.education || '',
// // // // // // //       custom_overall_experience_years: data.experience || '',
// // // // // // //       resume: file,
// // // // // // //     }));

// // // // // // //     toast.success('Resume parsed & auto-filled');
// // // // // // //   } catch (err) {
// // // // // // //     console.error(err);
// // // // // // //     toast.error('Resume parsing failed');
// // // // // // //   } finally {
// // // // // // //     setParsing(false);
// // // // // // //   }
// // // // // // // };


// // // // // // //   /* ================= SUBMIT ================= */

// // // // // // //   const handleSubmit = async (e) => {
// // // // // // //     e.preventDefault();

// // // // // // //     try {
// // // // // // //       const fd = new FormData();
// // // // // // //       Object.entries(formData).forEach(([k, v]) => {
// // // // // // //         if (v) fd.append(k, v);
// // // // // // //       });

// // // // // // //       await candidatesAPI.create(fd);
// // // // // // //       toast.success('Candidate added');

// // // // // // //       setShowForm(false);
// // // // // // //       setFormData({
// // // // // // //         first_name: '',
// // // // // // //         last_name: '',
// // // // // // //         email_id: '',
// // // // // // //         phone_number: '',
// // // // // // //         position: '',
// // // // // // //         skills: '',
// // // // // // //         education: '',
// // // // // // //         custom_current_employer: '',
// // // // // // //         custom_overall_experience_years: '',
// // // // // // //         custom_relevant_experience_years: '',
// // // // // // //         custom_current_salary_lpa: '',
// // // // // // //         custom_expected_salary_lpa: '',
// // // // // // //         notice_period: '',
// // // // // // //         resume: null,
// // // // // // //       });

// // // // // // //       setResumeFileName('');
// // // // // // //       fetchCandidates();
// // // // // // //     } catch {
// // // // // // //       toast.error('Failed to create candidate');
// // // // // // //     }
// // // // // // //   };

// // // // // // //   /* ================= UI ================= */

// // // // // // //   return (
// // // // // // //     <div className="card">
// // // // // // //       <div className="flex-between">
// // // // // // //         <h2>Candidates</h2>
// // // // // // //         <button onClick={() => setShowForm(!showForm)}>
// // // // // // //           {showForm ? 'Cancel' : 'Add Candidate'}
// // // // // // //         </button>
// // // // // // //       </div>

// // // // // // //       {showForm && (
// // // // // // //         <form onSubmit={handleSubmit}>
// // // // // // //           <label>Resume Upload</label>
// // // // // // //           <input
// // // // // // //             type="file"
// // // // // // //             accept=".pdf,.doc,.docx"
// // // // // // //             onChange={handleResumeUpload}
// // // // // // //           />
// // // // // // //           {resumeFileName && <p>Selected: {resumeFileName}</p>}

// // // // // // //           <input
// // // // // // //             name="first_name"
// // // // // // //             value={formData.first_name}
// // // // // // //             onChange={handleInputChange}
// // // // // // //             placeholder="First Name"
// // // // // // //             required
// // // // // // //           />

// // // // // // //           <input
// // // // // // //             name="last_name"
// // // // // // //             value={formData.last_name}
// // // // // // //             onChange={handleInputChange}
// // // // // // //             placeholder="Last Name"
// // // // // // //             required
// // // // // // //           />

// // // // // // //           <input
// // // // // // //             name="email_id"
// // // // // // //             value={formData.email_id}
// // // // // // //             onChange={handleInputChange}
// // // // // // //             placeholder="Email"
// // // // // // //             required
// // // // // // //           />

// // // // // // //           <input
// // // // // // //             name="phone_number"
// // // // // // //             value={formData.phone_number}
// // // // // // //             onChange={handleInputChange}
// // // // // // //             placeholder="Phone"
// // // // // // //           />

// // // // // // //           <input
// // // // // // //             name="skills"
// // // // // // //             value={formData.skills}
// // // // // // //             onChange={handleInputChange}
// // // // // // //             placeholder="Skills"
// // // // // // //           />

// // // // // // //           <button type="submit" disabled={parsing}>
// // // // // // //             {parsing ? 'Parsing Resume...' : 'Add Candidate'}
// // // // // // //           </button>
// // // // // // //         </form>
// // // // // // //       )}

// // // // // // //       {!loading && (
// // // // // // //         <table>
// // // // // // //           <thead>
// // // // // // //             <tr>
// // // // // // //               <th>Name</th>
// // // // // // //               <th>Email</th>
// // // // // // //               <th>Phone</th>
// // // // // // //               <th>Position</th>
// // // // // // //               <th>Action</th>
// // // // // // //             </tr>
// // // // // // //           </thead>
// // // // // // //           <tbody>
// // // // // // //             {candidates.map((c) => (
// // // // // // //               <tr key={c.id}>
// // // // // // //                 <td>{c.first_name} {c.last_name}</td>
// // // // // // //                 <td>{c.email_id}</td>
// // // // // // //                 <td>{c.phone_number}</td>
// // // // // // //                 <td>{c.position}</td>
// // // // // // //                 <td>
// // // // // // //                   <button onClick={() => navigate(`/candidates/${c.id}`)}>
// // // // // // //                     View
// // // // // // //                   </button>
// // // // // // //                 </td>
// // // // // // //               </tr>
// // // // // // //             ))}
// // // // // // //           </tbody>
// // // // // // //         </table>
// // // // // // //       )}
// // // // // // //     </div>
// // // // // // //   );
// // // // // // // }
